'use client';

import { attendantMediaItemPrivateOnly } from '@bells/i18n';
import { AttendantSearchSingleResponse } from '@bells/schema/models/attendant';
import { AspectRatio } from '@bells/ui/components/aspect-ratio';
import { Gem, Play, Video } from 'lucide-react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
const VideoPlayer = dynamic(() => import('@bells/ui/components/video-player'), {
  loading: () => <Skeleton className='h-full w-full'></Skeleton>,
  ssr: false,
});

import { Skeleton } from '@bells/ui/components/skeleton';
import React, { useEffect, useRef, useState } from 'react';
import GirlPicturesModal, { GirlPictureModalRef } from './girl-desktop-mobile';
import { cn } from '@bells/core/lib/utils';
import Link from 'next/link';
import { baseLocale } from '@bells/i18n/runtime';
import { useLanguage } from '@bells/core/i18n';

function smoothScrollTo(
  container: HTMLElement,
  targetTop: number,
  duration = 400
) {
  const start = container.scrollTop;
  const change = targetTop - start;
  const startTime = performance.now();

  function animateScroll(currentTime: number) {
    const time = Math.min(1, (currentTime - startTime) / duration);
    const easedTime = easeInOutQuad(time);

    container.scrollTop = start + change * easedTime;

    if (time < 1) {
      requestAnimationFrame(animateScroll);
    }
  }

  function easeInOutQuad(t: number) {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  }

  requestAnimationFrame(animateScroll);
}
export const GirLSlideshow = ({
  data,
}: {
  data?: AttendantSearchSingleResponse;
}) => {
  const [selectedImage, setSelectedImage] = useState<number | undefined>(
    data?.media && data.media.length > 0 ? 0 : undefined
  );

  const modalRef = useRef<GirlPictureModalRef | null>(null);

  const { language } = useLanguage();
  return (
    <div className='grid grid-cols-3 gap-1 lg:gap-4'>
      {data ? (
        data.media &&
        data.media.length > 0 &&
        data.media?.map((image, index) => {
          return (
            <React.Fragment key={'gallery-' + index}>
              {image?.type == 'image' && image.isVisible && (
                <AspectRatio asChild ratio={2 / 3}>
                  <button
                    onClick={() => {
                      modalRef?.current?.setOpenWithImage(index);
                    }}
                    className='relative overflow-hidden bg-muted lg:rounded-md'
                  >
                    <Image
                      placeholder={image.blurThumbnail ? 'blur' : undefined}
                      blurDataURL={image.blurThumbnail ?? undefined}
                      src={image.lg}
                      alt={`Image ${(selectedImage ?? 0) + 1}`}
                      width={0}
                      height={0}
                      unoptimized
                      fill
                      loading='lazy'
                      className={cn(
                        'cursor-pointer object-cover transition-transform duration-200 hover:scale-110'
                      )}
                    />
                  </button>
                </AspectRatio>
              )}

              {image?.type == 'video' && image.path && image.isVisible && (
                <AspectRatio asChild ratio={2 / 3}>
                  <button
                    onClick={() => {
                      modalRef?.current?.setOpenWithImage(index);
                    }}
                    className='relative overflow-hidden bg-muted lg:rounded-md'
                  >
                    <Image
                      alt={`Video ${(selectedImage ?? 0) + 1}`}
                      fill
                      unoptimized
                      loading='lazy'
                      src={image.thumbnail}
                      className='h-full w-full object-cover transition-transform duration-200 hover:scale-110'
                    ></Image>

                    <div className='z-2 w absolute right-0 top-0 flex w-full select-none flex-col items-end justify-start gap-2 p-4 text-center text-xs text-white lg:text-base'>
                      <Video
                        fill='currentColor'
                        className='size-4 lg:size-6'
                      ></Video>
                    </div>
                  </button>
                </AspectRatio>
              )}

              {image?.type == 'image' && !image.isVisible && (
                <AspectRatio asChild ratio={2 / 3}>
                  <Link
                    href={
                      baseLocale != language
                        ? `/${language}/auth/signin/customer`
                        : `/auth/signin/customer`
                    }
                    className='relative overflow-hidden bg-muted lg:rounded-md'
                  >
                    <Image
                      alt={`Image ${(selectedImage ?? 0) + 1}`}
                      fill
                      unoptimized
                      loading='lazy'
                      placeholder={image.blurThumbnail ? 'blur' : undefined}
                      src={image.lg}
                      blurDataURL={image.blurThumbnail ?? undefined}
                      className='h-full w-full object-cover'
                    ></Image>

                    <div className='z-2 absolute left-0 top-0 flex h-full w-full select-none flex-col items-center justify-center gap-2 bg-white/50 p-4 text-center text-xs lg:text-base'>
                      <Gem></Gem>
                      {attendantMediaItemPrivateOnly()}
                    </div>
                  </Link>
                </AspectRatio>
              )}

              {image?.type == 'video' && !image.isVisible && (
                <AspectRatio asChild ratio={2 / 3}>
                  <Link
                    href={
                      baseLocale != language
                        ? `/${language}/auth/signin/customer`
                        : `/auth/signin/customer`
                    }
                    className='relative overflow-hidden bg-muted transition-transform duration-200 lg:rounded-md'
                  >
                    <Image
                      fill
                      unoptimized
                      loading='lazy'
                      src={image.thumbnail}
                      className='h-full w-full object-cover hover:scale-105'
                      alt=''
                    ></Image>

                    <div className='z-2 absolute left-0 top-0 flex h-full w-full select-none flex-col items-center justify-center gap-2 bg-white/50 p-4 text-center text-xs lg:text-base'>
                      <Gem></Gem>
                      {attendantMediaItemPrivateOnly()}
                    </div>
                  </Link>
                </AspectRatio>
              )}
            </React.Fragment>
          );
        })
      ) : (
        <>
          <AspectRatio asChild ratio={2 / 3}>
            <Skeleton></Skeleton>
          </AspectRatio>
          <AspectRatio asChild ratio={2 / 3}>
            <Skeleton></Skeleton>
          </AspectRatio>
          <AspectRatio asChild ratio={2 / 3}>
            <Skeleton></Skeleton>
          </AspectRatio>
          <AspectRatio asChild ratio={2 / 3}>
            <Skeleton></Skeleton>
          </AspectRatio>
          <AspectRatio asChild ratio={2 / 3}>
            <Skeleton></Skeleton>
          </AspectRatio>
          <AspectRatio asChild ratio={2 / 3}>
            <Skeleton></Skeleton>
          </AspectRatio>
        </>
      )}

      {data && (
        <GirlPicturesModal ref={modalRef} data={data}></GirlPicturesModal>
      )}
    </div>
  );
};
